import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { formatWholeGrams } from '@/lib/format/format-weight';
import { useTranslation } from '@/hooks/use-translation';
import { AdjustmentSheetPreview } from './figures/AdjustmentSheetPreview';
import { DEMO_DESIGN, DEMO_PIANO_NAME } from './figures/demo-sample';
import { FrontWeightPreview } from './figures/FrontWeightPreview';
import { SpringsPreview } from './figures/SpringsPreview';
import { StaticBalancePreview } from './figures/StaticBalancePreview';
import { StrikeWeightPreview } from './figures/StrikeWeightPreview';

/**
 * The panel every card opens with: a fixed band of the page colour, so the six
 * figures line up whatever they are made of.
 */
const FigurePanel = t0 => {
  const $ = _c(5);
  const {
    children,
    header
  } = t0;
  let t1;
  if ($[0] !== header) {
    t1 = <figcaption className="flex min-h-4 items-center justify-between gap-2">{header}</figcaption>;
    $[0] = header;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <figure className="m-0 flex h-[200px] flex-col gap-1.5 border-b border-border bg-background px-4 pt-3.5 pb-3">{t1}{children}</figure>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const FigureCard = t0 => {
  const $ = _c(21);
  const {
    description,
    figure,
    header,
    linkLabel,
    section,
    title,
    to
  } = t0;
  let t1;
  if ($[0] !== figure || $[1] !== header) {
    t1 = <FigurePanel header={header}>{figure}</FigurePanel>;
    $[0] = figure;
    $[1] = header;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== title) {
    t2 = <h3 className="m-0 font-serif text-[26px] leading-[1.1] font-medium">{title}</h3>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== description) {
    t3 = <p className="m-0 flex-1 text-[15px] leading-[1.6] text-muted-foreground">{description}</p>;
    $[5] = description;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== section) {
    t4 = <span className="ptl-label text-ptl-ink-3">{section} ›</span>;
    $[7] = section;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowRight className="size-[15px]" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== linkLabel || $[11] !== t4 || $[12] !== to) {
    t6 = <Link to={to} className="ptl-link inline-flex items-center gap-1.5 pt-2 text-[14px] font-semibold">{t4}{linkLabel}{t5}</Link>;
    $[10] = linkLabel;
    $[11] = t4;
    $[12] = to;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t6) {
    t7 = <div className="flex flex-1 flex-col gap-2.5 px-6 pt-[22px] pb-6">{t2}{t3}{t6}</div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t1 || $[19] !== t7) {
    t8 = <article className="flex flex-col overflow-hidden rounded border border-border bg-card">{t1}{t7}</article>;
    $[18] = t1;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
const FigureLabel = t0 => {
  const $ = _c(2);
  const {
    number
  } = t0;
  const t1 = `Fig. ${number}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="ptl-label">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};

/** The small monospaced caption a figure carries at its top right. */
const FigureNote = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="truncate font-mono text-[10px] text-ptl-ink-3">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/** A series name beside the swatch that stands for it in the figure. */
const FigureKey = t0 => {
  const $ = _c(3);
  const {
    children,
    swatch
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== swatch) {
    t1 = <span className="inline-flex items-center gap-[5px] font-mono text-[10px] text-ptl-ink-3">{swatch}{children}</span>;
    $[0] = children;
    $[1] = swatch;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/** Chevrons to walk a list of curves, signs to raise or lower a weight. */
const STEPPER_ARROWS = {
  browse: ['‹', '›'],
  count: ['−', '+']
};

/**
 * The control the figure stands for, drawn as the page draws it but inert: the
 * card is a picture of the screen it opens, not a second way to drive it.
 */
const FigureStepper = t0 => {
  const $ = _c(10);
  const {
    arrows,
    children
  } = t0;
  const t1 = STEPPER_ARROWS[arrows];
  let t2;
  if ($[0] !== t1[0]) {
    t2 = <span className="flex h-full w-6 items-center justify-center text-[14px] text-muted-foreground">{t1[0]}</span>;
    $[0] = t1[0];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <span className="flex h-full items-center border-x border-border px-2 text-[12px] font-semibold">{children}</span>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = STEPPER_ARROWS[arrows];
  let t5;
  if ($[4] !== t4[1]) {
    t5 = <span className="flex h-full w-6 items-center justify-center text-[14px] text-muted-foreground">{t4[1]}</span>;
    $[4] = t4[1];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5) {
    t6 = <span aria-hidden="true" className="inline-flex h-[26px] items-center overflow-hidden rounded border border-input bg-card font-mono">{t2}{t3}{t5}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

/**
 * What the application does, in six panels.
 *
 * Each one is a real extract of the demonstration piano rather than a
 * drawing of one: the page claims to turn measurements into readable
 * figures, so the figures had better be the real thing.
 */
export const HomeFeatures = () => {
  const $ = _c(169);
  const {
    currentLanguage,
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("Main features");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="ptl-label">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <em className="ptl-accent"> then improve it</em>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = <h2 className="m-0 font-serif text-[clamp(34px,3.6vw,48px)] leading-[1.1] font-medium text-balance"><Trans t={t} i18nKey="Understand your action, <1>then improve it</1>">Understand your action,{t2}</Trans></h2>;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("You take a few weights on every key. Piano Touch Lab turns them into readable figures, then works out the adjustments that will even the touch out from one end of the keyboard to the other.");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <p className="m-0 max-w-[60ch] text-[16.5px] leading-[1.65] text-muted-foreground">{t4}</p>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t5) {
    t6 = <header className="flex flex-col items-center gap-4 text-center">{t1}{t3}{t5}</header>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FigureLabel number="1" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FigureKey swatch={<svg aria-hidden="true" width="9" height="8" viewBox="0 0 10 9"><path d="M0 0h10L5 9z" className="fill-ptl-series-down" /></svg>}>D</FigureKey>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FigureKey swatch={<svg aria-hidden="true" width="9" height="9" viewBox="0 0 10 10"><path d="M5 0l5 5-5 5-5-5z" className="fill-ptl-series-balance" /></svg>}>BW</FigureKey>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FigureKey swatch={<svg aria-hidden="true" width="9" height="8" viewBox="0 0 10 9"><path d="M0 9h10L5 0z" className="fill-ptl-series-up" /></svg>}>U</FigureKey>;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <>{t7}<span className="flex items-center gap-[9px]">{t8}{t9}{t10}<FigureKey swatch={<svg aria-hidden="true" width="8" height="8" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4.5" className="fill-ptl-series-friction" /></svg>}>F</FigureKey></span></>;
    t12 = <StaticBalancePreview />;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t) {
    t13 = t("Visual analysis");
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t) {
    t14 = t("Enter your measurements and explore the charts to spot irregularities in your action.");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t) {
    t15 = t("Touch Weight", {
      ns: "navigation"
    });
    $[25] = t;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t) {
    t16 = t("Analyze", {
      ns: "navigation"
    });
    $[27] = t;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t13 || $[30] !== t14 || $[31] !== t15 || $[32] !== t16) {
    t17 = <FigureCard header={t11} figure={t12} title={t13} description={t14} linkLabel={t15} section={t16} to="/analyze/touch-weight" />;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <><FigureLabel number="2" /><FigureStepper arrows="browse">{DEMO_DESIGN.strikeWeightLevel}</FigureStepper></>;
    t19 = <StrikeWeightPreview />;
    $[34] = t18;
    $[35] = t19;
  } else {
    t18 = $[34];
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t) {
    t20 = t("Interactive modeling");
    $[36] = t;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t) {
    t21 = t("Adjust the parameters in interactive charts, see their effect on the touch at once, and build your ideal keyboard step by step.");
    $[38] = t;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t) {
    t22 = t("Strike Weight", {
      ns: "navigation"
    });
    $[40] = t;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t) {
    t23 = t("Design", {
      ns: "navigation"
    });
    $[42] = t;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t20 || $[45] !== t21 || $[46] !== t22 || $[47] !== t23) {
    t24 = <FigureCard header={t18} figure={t19} title={t20} description={t21} linkLabel={t22} section={t23} to="/design/strike-weight" />;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <FigureLabel number="3" />;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t) {
    t26 = t("Target balance");
    $[50] = t;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t26) {
    t27 = <FigureNote>{t26}</FigureNote>;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== currentLanguage) {
    t28 = formatWholeGrams(DEMO_DESIGN.balanceWeightTarget, currentLanguage);
    $[54] = currentLanguage;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t28) {
    t29 = <FigureStepper arrows="count">{t28}</FigureStepper>;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t27 || $[59] !== t29) {
    t30 = <>{t25}<span className="flex min-w-0 items-center gap-2">{t27}{t29}</span></>;
    $[58] = t27;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <FrontWeightPreview />;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t) {
    t32 = t("Balance weight targeting");
    $[62] = t;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] !== t) {
    t33 = t("Compute the ideal weight of your keys or your hammers from the balance weight you are aiming for.");
    $[64] = t;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t) {
    t34 = t("Front Weight", {
      ns: "navigation"
    });
    $[66] = t;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== t) {
    t35 = t("Design", {
      ns: "navigation"
    });
    $[68] = t;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== t30 || $[71] !== t32 || $[72] !== t33 || $[73] !== t34 || $[74] !== t35) {
    t36 = <FigureCard header={t30} figure={t31} title={t32} description={t33} linkLabel={t34} section={t35} to="/design/front-weight" />;
    $[70] = t30;
    $[71] = t32;
    $[72] = t33;
    $[73] = t34;
    $[74] = t35;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <FigureLabel number="4" />;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  let t38;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <span className="inline-block h-2 w-1.5 bg-ptl-brass opacity-60" />;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== t) {
    t39 = t("designed");
    $[78] = t;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  let t40;
  if ($[80] !== t39) {
    t40 = <FigureKey swatch={t38}>{t39}</FigureKey>;
    $[80] = t39;
    $[81] = t40;
  } else {
    t40 = $[81];
  }
  let t41;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <span className="inline-block size-[5px] rounded-full bg-ptl-series-balance" />;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== t) {
    t42 = t("measured");
    $[83] = t;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] !== t42) {
    t43 = <FigureKey swatch={t41}>{t42}</FigureKey>;
    $[85] = t42;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t40 || $[88] !== t43) {
    t44 = <>{t37}<span className="flex items-center gap-2.5">{t40}{t43}</span></>;
    $[87] = t40;
    $[88] = t43;
    $[89] = t44;
  } else {
    t44 = $[89];
  }
  let t45;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <SpringsPreview />;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== t) {
    t46 = t("Wippen Support Springs", {
      ns: "navigation"
    });
    $[91] = t;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== t) {
    t47 = t("If your wippens carry support springs, work them into your design to reduce the lead needed in the keys.");
    $[93] = t;
    $[94] = t47;
  } else {
    t47 = $[94];
  }
  let t48;
  if ($[95] !== t) {
    t48 = t("Wippen Support Springs", {
      ns: "navigation"
    });
    $[95] = t;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== t) {
    t49 = t("Design", {
      ns: "navigation"
    });
    $[97] = t;
    $[98] = t49;
  } else {
    t49 = $[98];
  }
  let t50;
  if ($[99] !== t44 || $[100] !== t46 || $[101] !== t47 || $[102] !== t48 || $[103] !== t49) {
    t50 = <FigureCard header={t44} figure={t45} title={t46} description={t47} linkLabel={t48} section={t49} to="/design/wippen-support-springs" />;
    $[99] = t44;
    $[100] = t46;
    $[101] = t47;
    $[102] = t48;
    $[103] = t49;
    $[104] = t50;
  } else {
    t50 = $[104];
  }
  let t51;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <FigureLabel number="5" />;
    $[105] = t51;
  } else {
    t51 = $[105];
  }
  let t52;
  if ($[106] !== t) {
    t52 = t("p. {{page}}/{{pages}}", {
      page: 1,
      pages: 4
    });
    $[106] = t;
    $[107] = t52;
  } else {
    t52 = $[107];
  }
  let t53;
  if ($[108] !== t52) {
    t53 = <>{t51}<FigureNote>{DEMO_PIANO_NAME} ·{" "}{t52}</FigureNote></>;
    $[108] = t52;
    $[109] = t53;
  } else {
    t53 = $[109];
  }
  let t54;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <AdjustmentSheetPreview />;
    $[110] = t54;
  } else {
    t54 = $[110];
  }
  let t55;
  if ($[111] !== t) {
    t55 = t("Adjustment sheets");
    $[111] = t;
    $[112] = t55;
  } else {
    t55 = $[112];
  }
  let t56;
  if ($[113] !== t) {
    t56 = t("Print a worksheet from your design, so every adjustment figure is at hand in your workshop.");
    $[113] = t;
    $[114] = t56;
  } else {
    t56 = $[114];
  }
  let t57;
  if ($[115] !== t) {
    t57 = t("Adjustment Sheet", {
      ns: "navigation"
    });
    $[115] = t;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  let t58;
  if ($[117] !== t) {
    t58 = t("Design", {
      ns: "navigation"
    });
    $[117] = t;
    $[118] = t58;
  } else {
    t58 = $[118];
  }
  let t59;
  if ($[119] !== t53 || $[120] !== t55 || $[121] !== t56 || $[122] !== t57 || $[123] !== t58) {
    t59 = <FigureCard header={t53} figure={t54} title={t55} description={t56} linkLabel={t57} section={t58} to="/design/adjustment-sheet" />;
    $[119] = t53;
    $[120] = t55;
    $[121] = t56;
    $[122] = t57;
    $[123] = t58;
    $[124] = t59;
  } else {
    t59 = $[124];
  }
  let t60;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <FigureLabel number="6" />;
    $[125] = t60;
  } else {
    t60 = $[125];
  }
  let t61;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <>0 €</>;
    $[126] = t61;
  } else {
    t61 = $[126];
  }
  let t62;
  if ($[127] !== t) {
    t62 = t("Free");
    $[127] = t;
    $[128] = t62;
  } else {
    t62 = $[128];
  }
  let t63;
  if ($[129] !== t62) {
    t63 = {
      figure: t61,
      label: t62
    };
    $[129] = t62;
    $[130] = t63;
  } else {
    t63 = $[130];
  }
  let t64;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <>0</>;
    $[131] = t64;
  } else {
    t64 = $[131];
  }
  let t65;
  if ($[132] !== t) {
    t65 = t("account to create");
    $[132] = t;
    $[133] = t65;
  } else {
    t65 = $[133];
  }
  let t66;
  if ($[134] !== t65) {
    t66 = {
      figure: t64,
      label: t65
    };
    $[134] = t65;
    $[135] = t66;
  } else {
    t66 = $[135];
  }
  let t67;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <>100 %</>;
    $[136] = t67;
  } else {
    t67 = $[136];
  }
  let t68;
  if ($[137] !== t) {
    t68 = t("in your browser");
    $[137] = t;
    $[138] = t68;
  } else {
    t68 = $[138];
  }
  let t69;
  if ($[139] !== t68) {
    t69 = {
      figure: t67,
      label: t68
    };
    $[139] = t68;
    $[140] = t69;
  } else {
    t69 = $[140];
  }
  let t70;
  if ($[141] !== t63 || $[142] !== t66 || $[143] !== t69) {
    t70 = <FigurePanel header={t60}><div className="grid flex-1 grid-cols-3 items-center text-center">{[t63, t66, t69].map(_temp)}</div></FigurePanel>;
    $[141] = t63;
    $[142] = t66;
    $[143] = t69;
    $[144] = t70;
  } else {
    t70 = $[144];
  }
  let t71;
  if ($[145] !== t) {
    t71 = t("Free and respectful of your privacy");
    $[145] = t;
    $[146] = t71;
  } else {
    t71 = $[146];
  }
  let t72;
  if ($[147] !== t71) {
    t72 = <h3 className="m-0 font-serif text-[26px] leading-[1.1] font-medium">{t71}</h3>;
    $[147] = t71;
    $[148] = t72;
  } else {
    t72 = $[148];
  }
  let t73;
  if ($[149] !== t) {
    t73 = t("No account is required, all your data is stored locally in your browser and never leaves your computer.");
    $[149] = t;
    $[150] = t73;
  } else {
    t73 = $[150];
  }
  let t74;
  if ($[151] !== t73) {
    t74 = <p className="m-0 text-[15px] leading-[1.6] text-muted-foreground">{t73}</p>;
    $[151] = t73;
    $[152] = t74;
  } else {
    t74 = $[152];
  }
  let t75;
  if ($[153] !== t72 || $[154] !== t74) {
    t75 = <div className="flex flex-1 flex-col gap-2.5 px-6 pt-[22px] pb-6">{t72}{t74}</div>;
    $[153] = t72;
    $[154] = t74;
    $[155] = t75;
  } else {
    t75 = $[155];
  }
  let t76;
  if ($[156] !== t70 || $[157] !== t75) {
    t76 = <article className="flex flex-col overflow-hidden rounded border border-border bg-card">{t70}{t75}</article>;
    $[156] = t70;
    $[157] = t75;
    $[158] = t76;
  } else {
    t76 = $[158];
  }
  let t77;
  if ($[159] !== t17 || $[160] !== t24 || $[161] !== t36 || $[162] !== t50 || $[163] !== t59 || $[164] !== t76) {
    t77 = <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-5">{t17}{t24}{t36}{t50}{t59}{t76}</div>;
    $[159] = t17;
    $[160] = t24;
    $[161] = t36;
    $[162] = t50;
    $[163] = t59;
    $[164] = t76;
    $[165] = t77;
  } else {
    t77 = $[165];
  }
  let t78;
  if ($[166] !== t6 || $[167] !== t77) {
    t78 = <section className="border-b border-border px-12 pt-[104px] pb-28 max-md:px-4 max-md:py-14"><div className="mx-auto flex max-w-[1120px] flex-col gap-12">{t6}{t77}</div></section>;
    $[166] = t6;
    $[167] = t77;
    $[168] = t78;
  } else {
    t78 = $[168];
  }
  return t78;
};
function _temp(stat, index) {
  return <div key={stat.label} className={`flex flex-col items-center gap-2 px-1.5 ${index === 0 ? "" : "border-l border-border"}`}><span className="font-serif text-[36px] leading-none font-medium whitespace-nowrap text-ptl-brass">{stat.figure}</span><span className="ptl-label text-[10px] leading-[1.4] tracking-[0.06em]">{stat.label}</span></div>;
}